import { useState, useEffect, useRef } from "react";
import { useTutorProfile } from "@/hooks/useTutorProfile";
import { useTutorProfileApproval } from "@/hooks/useTutorProfileApproval";
import { useSubjects } from "@/hooks/useSubjects";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  X,
  Plus,
  Clock,
  Trash2,
  AlertTriangle,
  BookOpen,
  MapPin,
  Camera,
  Upload,
  Link,
  CreditCard,
  MessageCircle,
  GraduationCap,
  DollarSign,
  CheckCircle,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { pb } from "@/lib/pocketbase";
import { useAuth } from "@/hooks/useAuth";
import { countries, getCountryByCode } from "@/utils/countries";
import { useCurrency, currencies } from "@/contexts/CurrencyContext";
import { detectUserCurrency } from "@/utils/locationDetection";
import { hasMultipleTimezones, getCountryTimezones, getTimezoneDisplayName } from "@/utils/countryTimezones";
import { CountryCombobox } from "@/components/CountryCombobox";

interface Education {
  degree: string;
  institution: string;
  year: number;
}

interface TimeSlot {
  start: string;
  end: string;
}

interface DaySlot {
  day: string;
  slots: TimeSlot[];
}

interface Availability {
  days: string[];
  timeSlots: DaySlot[];
}

interface TutorData {
  id: string;
  name: string;
  profilePicture: string;
  bio: string;
  shortBio: string;
  subjects: string[];
  hourlyRate: number;
  rating: number;
  reviewCount: number;
  languages: string[];
  experience: string;
  education: Education[];
  availability: Availability;
  introVideo: string;
  country: string;
  timezone?: string; // Optional timezone for countries with multiple timezones
  personalEmail: string;
  phoneNumber: string;
  sessionPreference: "chat_only" | "video_only" | "both";
  pricingType: "hourly" | "session" | "level" | "monthly";
  sessionRate?: number;
  levelRates?: {
    beginner: number;
    intermediate: number;
    advanced: number;
  };
  monthlyRate?: number;
  paymentMethods?: string[];
  grades?: string[];
  pricingCurrency?: string; // Add currency field
}

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  tutorData: TutorData;
  onSave?: () => void;
  onShowSuccessModal?: () => void;
  scrollToSection?: string;
}

const subjectCategories = {
  Academic: ["Mathematics", "English", "Spanish", "Physics", "Chemistry", "Biology", "Literature"],
  Languages: [
    "English Language",
    "Spanish",
    "French",
    "German",
    "Italian",
    "Portuguese",
    "Chinese",
    "Japanese",
    "Korean",
    "Arabic",
  ],
  Professional: [
    "Programming",
    "Business",
    "Marketing",
    "Finance",
    "Data Science",
    "Web Development",
    "Graphic Design",
  ],
  "Life Skills": ["Fitness & Bodybuilding", "Cooking", "Music", "Art", "Photography", "Travel", "Conversation"],
};

const EditProfileModal = ({
  isOpen,
  onClose,
  tutorData,
  onSave,
  onShowSuccessModal,
  scrollToSection,
}: EditProfileModalProps) => {
  const { updateProfile, getAllAvailableSubjects } = useTutorProfile();
  const { subjects, refetch: refetchSubjects } = useSubjects(); // Add refetch to force update
  const { user } = useAuth();
  const { toast } = useToast();

  // Section refs for scrolling
  const photoSectionRef = useRef<HTMLDivElement>(null);
  const contactSectionRef = useRef<HTMLDivElement>(null);
  const shortBioSectionRef = useRef<HTMLDivElement>(null);
  const bioSectionRef = useRef<HTMLDivElement>(null);
  const subjectsSectionRef = useRef<HTMLDivElement>(null);
  const languagesSectionRef = useRef<HTMLDivElement>(null);
  const educationSectionRef = useRef<HTMLDivElement>(null);
  const experienceSectionRef = useRef<HTMLDivElement>(null);
  const availabilitySectionRef = useRef<HTMLDivElement>(null);
  const sessionSectionRef = useRef<HTMLDivElement>(null);
  const pricingSectionRef = useRef<HTMLDivElement>(null);
  const videoSectionRef = useRef<HTMLDivElement>(null);

  // Generate a unique storage key for this tutor's draft
  const getDraftKey = () => {
    const userId = user?.id || tutorData.id || "anonymous";
    return `tutorProfileDraft_${userId}`;
  };

  // Initialize formData with saved draft or default values
  const [formData, setFormData] = useState(() => {
    // Try to load saved draft first
    const savedDraft = localStorage.getItem(getDraftKey());
    if (savedDraft) {
      try {
        const parsedDraft = JSON.parse(savedDraft);
        console.log("📝 Loaded saved draft from localStorage");
        return {
          ...tutorData,
          ...parsedDraft,
          // Ensure nested objects are properly structured
          availability: parsedDraft.availability || {
            days: [],
            timeSlots: [],
          },
          levelRates: parsedDraft.levelRates || { beginner: 0, intermediate: 0, advanced: 0 },
        };
      } catch (error) {
        console.error("Error parsing saved draft:", error);
      }
    }

    // Return default values if no draft
    return {
      ...tutorData,
      name: tutorData.name || "",
      bio: tutorData.bio || "",
      shortBio: tutorData.shortBio || "",
      introVideo: tutorData.introVideo || "",
      experience: tutorData.experience || "",
      hourlyRate: tutorData.hourlyRate || 5,
      availability: {
        days: tutorData.availability?.days || [],
        timeSlots: tutorData.availability?.timeSlots || [],
      },
      subjects: tutorData.subjects || [],
      languages: tutorData.languages || [],
      education: tutorData.education || [],
      country: tutorData.country || "EG", // Default to Egypt
      timezone: tutorData.timezone || "",
      personalEmail: tutorData.personalEmail || "",
      phoneNumber: tutorData.phoneNumber || "",
      sessionPreference: tutorData.sessionPreference || "both", // Default to chat & video
      pricingType: tutorData.pricingType || "hourly", // Default to hourly pricing
      sessionRate: tutorData.sessionRate || 0,
      levelRates: tutorData.levelRates || { beginner: 0, intermediate: 0, advanced: 0 },
      monthlyRate: tutorData.monthlyRate || 0,
      paymentMethods: tutorData.paymentMethods || [],
      grades: tutorData.grades || [],
      pricingCurrency: tutorData.pricingCurrency || "USD",
      profilePicture: tutorData.profilePicture || "",
    };
  });

  const [approvedSubjects, setApprovedSubjects] = useState<string[]>([]);
  const [customSubject, setCustomSubject] = useState("");
  const [newLanguage, setNewLanguage] = useState("");
  const [newEducation, setNewEducation] = useState({ degree: "", institution: "", year: new Date().getFullYear() });
  const [isSaving, setIsSaving] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>("");
  const [photoUrl, setPhotoUrl] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedVideo, setSelectedVideo] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string>("");
  const [videoPreviewError, setVideoPreviewError] = useState<boolean>(false);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<
    { id: string; name: string; isEnabled: boolean }[]
  >([]);
  const [isLoadingModalData, setIsLoadingModalData] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [supportContacts, setSupportContacts] = useState<Array<{ id: string; phone_number: string; label: string }>>(
    [],
  );
  const [copiedContactId, setCopiedContactId] = useState<string | null>(null);

  // Get all available subjects (including approved ones)
  const availableSubjects = getAllAvailableSubjects();

  // Auto-save form data to localStorage whenever it changes
  useEffect(() => {
    if (!isOpen) return; // Don't save if modal is closed

    const timeoutId = setTimeout(() => {
      try {
        const draftKey = getDraftKey();
        localStorage.setItem(draftKey, JSON.stringify(formData));
        setLastSavedAt(new Date());
        console.log("💾 Auto-saved draft to localStorage");
      } catch (error) {
        console.error("Error auto-saving draft:", error);
      }
    }, 1000); // Debounce for 1 second

    return () => clearTimeout(timeoutId);
  }, [formData, isOpen]);

  // Auto-detect currency only for new profiles (first time creation) - TEMPORARILY DISABLED
  useEffect(() => {
    // Always ensure currency is USD - auto-detection disabled
    setFormData((prev) => ({
      ...prev,
      pricingCurrency: "USD",
    }));
  }, [isOpen]);

  // Fetch support contacts
  useEffect(() => {
    const fetchSupportContacts = async () => {
      try {
        const records = await pb.collection("support_contacts").getFullList({
          filter: "is_active = true",
          sort: "display_order,created",
        });
        setSupportContacts(
          records.map((r) => ({
            id: r.id,
            phone_number: r.phone_number,
            label: r.label,
          })),
        );
      } catch (error) {
        console.error("Error fetching support contacts:", error);
      }
    };

    if (isOpen) {
      fetchSupportContacts();
    }
  }, [isOpen]);

  // Fetch approved subjects and payment methods
  useEffect(() => {
    const fetchApprovedSubjects = async () => {
      try {
        // Fetch from both collections
        const [legacyRecords, subjectRequestsRecords] = await Promise.all([
          pb.collection("approved_subjects").getList(1, 100, {
            filter: "isActive = true",
            sort: "name",
          }),
          pb.collection("subject_requests").getList(1, 100, {
            filter: 'status = "approved"',
            sort: "subjectName",
          }),
        ]);

        const legacySubjects = legacyRecords.items.map((item) => item.name);
        const approvedRequestSubjects = subjectRequestsRecords.items.map((item) => item.subjectName);

        // Combine both sources
        const allApprovedSubjects = [...new Set([...legacySubjects, ...approvedRequestSubjects])];

        setApprovedSubjects(allApprovedSubjects);
      } catch (error) {
        console.log("Error fetching approved subjects:", error);
        setApprovedSubjects([]);
      }
    };

    const fetchPaymentMethods = async () => {
      try {
        const settings = await pb.collection("payment_method_settings").getList(1, 50, {
          filter: "isEnabled = true",
        });
        const methods = settings.items.map((item) => ({
          id: item.methodId,
          name: item.methodName, // Fixed: was item.displayName, should be item.methodName
          isEnabled: item.isEnabled,
        }));
        console.log("Fetched payment methods:", methods);
        setAvailablePaymentMethods(methods);
      } catch (error) {
        console.log("Error fetching payment methods:", error);
        // If there's an error or no payment methods collection exists, set default methods
        const defaultMethods = [
          { id: "paypal", name: "PayPal", isEnabled: true },
          { id: "bank_transfer", name: "Bank Transfer", isEnabled: true },
          { id: "wise", name: "Wise (formerly TransferWise)", isEnabled: true },
          { id: "stripe", name: "Credit/Debit Card (Stripe)", isEnabled: true },
          { id: "cash", name: "Cash (Local Sessions)", isEnabled: true },
        ];
        console.log("Using default payment methods:", defaultMethods);
        setAvailablePaymentMethods(defaultMethods);
      }
    };

    if (isOpen) {
      setIsLoadingModalData(true);

      Promise.all([fetchApprovedSubjects(), fetchPaymentMethods()]).finally(() => {
        setIsLoadingModalData(false);
      });
    }
  }, [isOpen]);

  // Scroll to section when modal opens
  useEffect(() => {
    if (isOpen && scrollToSection) {
      // Wait for modal to render
      setTimeout(() => {
        const sectionMap: Record<string, React.RefObject<HTMLDivElement>> = {
          photo: photoSectionRef,
          contact: contactSectionRef,
          shortBio: shortBioSectionRef,
          bio: bioSectionRef,
          about: bioSectionRef, // Alias for bio
          subjects: subjectsSectionRef,
          languages: languagesSectionRef,
          education: educationSectionRef,
          experience: experienceSectionRef,
          availability: availabilitySectionRef,
          session: sessionSectionRef,
          pricing: pricingSectionRef,
          video: videoSectionRef,
        };

        const targetRef = sectionMap[scrollToSection];
        if (targetRef?.current) {
          targetRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 300);
    }
  }, [isOpen, scrollToSection]);

  // Predefined options for dropdowns
  const availableLanguages = [
    "English",
    "Spanish",
    "French",
    "German",
    "Italian",
    "Portuguese",
    "Chinese (Mandarin)",
    "Chinese (Cantonese)",
    "Japanese",
    "Korean",
    "Arabic",
    "Russian",
    "Hindi",
    "Dutch",
    "Swedish",
    "Norwegian",
    "Danish",
    "Polish",
    "Turkish",
    "Hebrew",
    "Thai",
    "Vietnamese",
  ];

  const degreeOptions = [
    "High School Diploma",
    "Associate Degree",
    "Bachelor's Degree",
    "Master's Degree",
    "PhD/Doctorate",
    "Professional Certificate",
    "Teaching Certificate",
    "TEFL/TESOL Certificate",
  ];

  const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  const timeOptions = Array.from({ length: 24 }, (_, i) => {
    const hour = i.toString().padStart(2, "0");
    return [`${hour}:00`, `${hour}:30`];
  }).flat();

  const handleInputChange = (
    field: string,
    value: string | number | Education[] | Availability | string[] | object,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Track manual currency selection to prevent auto-detection override
    if (field === "pricingCurrency") {
      localStorage.setItem("tutorManualCurrencySelection", "true");
      console.log("Tutor manually selected currency:", value);
    }
  };

  const handleSubjectToggle = (subject: string, checked: boolean) => {
    if (checked) {
      if (!formData.subjects.includes(subject)) {
        setFormData((prev) => ({
          ...prev,
          subjects: [...prev.subjects, subject],
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        subjects: prev.subjects.filter((s) => s !== subject),
      }));
    }
  };

  const addCustomSubject = async () => {
    if (!customSubject.trim()) return;

    try {
      await pb.collection("subject_requests").create({
        tutorId: user?.id || "",
        tutorName: user?.name || tutorData.name,
        tutorEmail: user?.email || "",
        subjectName: customSubject.trim(),
        status: "pending",
        requestedAt: new Date().toISOString(),
      });

      // Send notification to admin about new subject request
      try {
        const { hybridNotificationService } = await import("@/services/hybridNotificationService");
        await hybridNotificationService.sendNotification({
          userId: "admin_notification",
          type: "general" as any,
          title: "New Subject Request",
          message: `${user?.name || tutorData.name} requested to add "${customSubject.trim()}" as a subject`,
        });
        console.log("Admin notification sent for new subject request");
      } catch (notificationError) {
        console.error("Failed to send admin notification:", notificationError);
      }

      toast({
        title: "Subject Request Submitted",
        description: `Your request for "${customSubject}" has been sent to admin for approval.`,
        variant: "default",
      });

      setCustomSubject("");
    } catch (error: any) {
      console.error("Error submitting subject request:", error);
      toast({
        title: "Error",
        description: "Failed to submit subject request. Please try again.",
        variant: "destructive",
      });
    }
  };

  const removeSubject = (subject: string) => {
    setFormData((prev) => ({
      ...prev,
      subjects: prev.subjects.filter((s) => s !== subject),
    }));
  };

  const addLanguage = () => {
    if (newLanguage && !formData.languages.includes(newLanguage)) {
      setFormData((prev) => ({
        ...prev,
        languages: [...prev.languages, newLanguage],
      }));
      setNewLanguage("");
    }
  };

  const removeLanguage = (language: string) => {
    setFormData((prev) => ({
      ...prev,
      languages: prev.languages.filter((l) => l !== language),
    }));
  };

  const addEducation = () => {
    if (newEducation.degree && newEducation.institution) {
      setFormData((prev) => ({
        ...prev,
        education: [...prev.education, { ...newEducation }],
      }));
      setNewEducation({ degree: "", institution: "", year: new Date().getFullYear() });
    }
  };

  const removeEducation = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index),
    }));
  };

  const addTimeSlot = (day: string) => {
    const existingDayIndex = formData.availability.timeSlots.findIndex((slot) => slot.day === day);

    if (existingDayIndex >= 0) {
      const updatedTimeSlots = [...formData.availability.timeSlots];
      updatedTimeSlots[existingDayIndex].slots.push({ start: "09:00", end: "10:00" });

      setFormData((prev) => ({
        ...prev,
        availability: {
          ...prev.availability,
          timeSlots: updatedTimeSlots,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        availability: {
          ...prev.availability,
          timeSlots: [
            ...prev.availability.timeSlots,
            {
              day,
              slots: [{ start: "09:00", end: "10:00" }],
            },
          ],
        },
      }));
    }
  };

  const removeTimeSlot = (dayIndex: number, slotIndex: number) => {
    const updatedTimeSlots = [...formData.availability.timeSlots];
    updatedTimeSlots[dayIndex].slots.splice(slotIndex, 1);

    if (updatedTimeSlots[dayIndex].slots.length === 0) {
      updatedTimeSlots.splice(dayIndex, 1);
    }

    setFormData((prev) => ({
      ...prev,
      availability: {
        ...prev.availability,
        timeSlots: updatedTimeSlots,
      },
    }));
  };

  const updateTimeSlot = (dayIndex: number, slotIndex: number, field: "start" | "end", value: string) => {
    const updatedTimeSlots = [...formData.availability.timeSlots];
    updatedTimeSlots[dayIndex].slots[slotIndex][field] = value;

    setFormData((prev) => ({
      ...prev,
      availability: {
        ...prev.availability,
        timeSlots: updatedTimeSlots,
      },
    }));
  };

  // Handle photo upload
  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: "Please select a JPEG, PNG, GIF, or WebP image.",
          variant: "destructive",
        });
        return;
      }

      // Check file size (5MB limit)
      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        toast({
          title: "File too large",
          description: "Please select an image smaller than 5MB.",
          variant: "destructive",
        });
        return;
      }

      setSelectedPhoto(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setSelectedPhoto(null);
    setPhotoPreview("");
    setPhotoUrl("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handlePhotoUrlSubmit = () => {
    if (photoUrl.trim()) {
      setPhotoPreview(photoUrl.trim());
      setSelectedPhoto(null); // Clear file selection when using URL
    }
  };

  const getCurrentPhotoUrl = () => {
    if (photoPreview) return photoPreview;
    if (tutorData.profilePicture) {
      return pb.files.getURL(tutorData, tutorData.profilePicture);
    }
    return "";
  };

  // Handle video upload
  const handleVideoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    console.log("📹 Video file selected:", file);

    if (file) {
      console.log("📹 File details:", {
        name: file.name,
        type: file.type,
        size: file.size,
      });

      // Allow all video types
      if (!file.type.startsWith("video/")) {
        console.error("📹 Not a video file:", file.type);
        toast({
          title: "Invalid file type",
          description: "Please select a video file.",
          variant: "destructive",
        });
        return;
      }

      // Check file size (500MB limit)
      const maxSize = 500 * 1024 * 1024;
      if (file.size > maxSize) {
        console.error("📹 File too large:", file.size);
        toast({
          title: "File too large",
          description: "Please select a video smaller than 500MB.",
          variant: "destructive",
        });
        return;
      }

      console.log("📹 Setting selected video...");
      setSelectedVideo(file);
      setVideoPreviewError(false);

      // Try to create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        console.log("📹 Video data URL created");
        setVideoPreview(result);
      };
      reader.onerror = () => {
        console.error("📹 FileReader error");
        setVideoPreviewError(true);
      };
      reader.readAsDataURL(file);

      // Clear the URL input
      handleInputChange("introVideo", "");

      toast({
        title: "Video selected",
        description: `${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB) ready to upload`,
      });
    }
  };

  const removeVideo = () => {
    setSelectedVideo(null);
    setVideoPreview("");
    setVideoPreviewError(false);
    handleInputChange("introVideo", "");
    if (videoInputRef.current) {
      videoInputRef.current.value = "";
    }
  };

  const { submitProfileUpdate, hasActivePendingUpdate, hasRejectedProfile } = useTutorProfileApproval();
  const [hasRejection, setHasRejection] = useState(false);

  // Check for rejected profiles on mount
  useEffect(() => {
    const checkRejection = async () => {
      if (user?.id && user.role === "tutor") {
        const rejected = await hasRejectedProfile(user.id);
        setHasRejection(rejected);
      }
    };
    checkRejection();
  }, [user?.id, user?.role]);

  // Check if all required fields are filled for tutors
  const isFormValid = () => {
    if (user?.role !== "tutor") return true;

    const hasPhoto = getCurrentPhotoUrl() || selectedPhoto || photoPreview;
    const hasAvailability = formData.availability.timeSlots && formData.availability.timeSlots.length > 0;
    const hasSessionPreference = formData.sessionPreference && formData.sessionPreference.trim() !== "";
    const hasPricing = validatePricing();
    const hasLanguages = formData.languages && formData.languages.length > 0;
    const hasCountry = formData.country && formData.country.trim() !== "";
    const hasEmail = formData.personalEmail && formData.personalEmail.trim() !== "";
    const hasPhone = formData.phoneNumber && formData.phoneNumber.trim() !== "";

    return (
      formData.name.trim() &&
      formData.shortBio.trim() &&
      formData.bio.trim() &&
      formData.subjects.length > 0 &&
      hasPhoto &&
      hasAvailability &&
      hasSessionPreference &&
      hasPricing &&
      hasLanguages &&
      hasCountry &&
      hasEmail &&
      hasPhone
    );
  };

  const getValidationErrors = () => {
    if (user?.role !== "tutor") return [];

    const errors = [];
    if (!formData.name.trim()) errors.push("Name is required");
    if (!formData.shortBio.trim()) errors.push("Short bio is required");
    if (!formData.bio.trim()) errors.push("Bio is required");

    if (!formData.country || formData.country.trim() === "") {
      errors.push("Country is required");
    }

    if (!formData.personalEmail || formData.personalEmail.trim() === "") {
      errors.push("Personal email is required");
    }

    if (!formData.phoneNumber || formData.phoneNumber.trim() === "") {
      errors.push("Phone number is required");
    }

    // Validate pricing based on type
    const pricingErrors = getPricingValidationErrors();
    errors.push(...pricingErrors);

    if (formData.subjects.length === 0) errors.push("At least one subject is required");

    if (!formData.languages || formData.languages.length === 0) {
      errors.push("At least one teaching language is required");
    }

    const hasPhoto = getCurrentPhotoUrl() || selectedPhoto || photoPreview;
    if (!hasPhoto) errors.push("Profile picture is required");

    const hasAvailability = formData.availability.timeSlots && formData.availability.timeSlots.length > 0;
    if (!hasAvailability) errors.push("At least one time slot is required");

    if (!formData.sessionPreference || formData.sessionPreference.trim() === "") {
      errors.push("Session preference is required");
    }

    return errors;
  };

  // Validation error for subjects
  const getSubjectValidationError = () => {
    if (user?.role !== "tutor") return "";
    if (formData.subjects.length === 0) {
      return "At least one subject is required";
    }
    return "";
  };

  // Validation error for availability
  const getAvailabilityValidationError = () => {
    if (user?.role !== "tutor") return "";
    if (!formData.availability.timeSlots || formData.availability.timeSlots.length === 0) {
      return "At least one time slot is required";
    }
    return "";
  };

  const handleSave = async () => {
    setIsSaving(true);

    // Show immediate feedback
    toast({
      title: "Submitting...",
      description: "Your profile update is being processed. This may take a few moments.",
      duration: 3000,
    });

    try {
      console.log("Saving profile with email and phone:", {
        personalEmail: formData.personalEmail,
        phoneNumber: formData.phoneNumber,
      });

      // Prepare the updated data
      const updatedData: any = {
        name: formData.name,
        bio: formData.bio,
        shortBio: formData.shortBio,
        subjects: formData.subjects,
        hourlyRate: formData.hourlyRate,
        pricingType: formData.pricingType,
        sessionRate: formData.sessionRate,
        levelRates: formData.levelRates,
        monthlyRate: formData.monthlyRate,
        pricingCurrency: formData.pricingCurrency,
        languages: formData.languages,
        experience: formData.experience,
        education: formData.education,
        availability: formData.availability,
        introVideo: formData.introVideo,
        country: formData.country,
        timezone: formData.timezone || null,
        personalEmail: formData.personalEmail.trim() || null,
        phoneNumber: formData.phoneNumber.trim() || null,
        sessionPreference: formData.sessionPreference,
        paymentMethods: formData.paymentMethods,
        grades: formData.grades,
      };

      // Add profile picture information if changed
      if (selectedPhoto) {
        updatedData.profilePicture = selectedPhoto;
        updatedData.hasNewPhoto = true;
      } else if (photoUrl.trim() && photoPreview) {
        updatedData.profilePictureUrl = photoUrl.trim();
        updatedData.hasNewPhoto = true;
      }

      // Add video file if uploaded
      if (selectedVideo) {
        updatedData.introVideoFile = selectedVideo;
        updatedData.hasNewVideo = true;
      }

      // Submit profile update for admin approval
      // Changes will be applied to the tutors collection only after admin approval
      const updateType = selectedPhoto ? "profile_picture" : "profile_edit";
      await submitProfileUpdate(tutorData.id, tutorData, updatedData, updateType);

      // Clear the saved draft after successful submission
      const draftKey = getDraftKey();
      localStorage.removeItem(draftKey);
      console.log("🗑️ Cleared draft from localStorage after successful submission");

      // Close edit modal and trigger success modal
      onClose();

      // Show success modal after edit modal is closed
      setTimeout(() => {
        onShowSuccessModal?.();
      }, 100);
    } catch (error: any) {
      console.error("Error saving profile:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Validate pricing based on selected type - all required fields
  const validatePricing = () => {
    // Pricing type is mandatory
    if (!formData.pricingType || formData.pricingType.trim() === "") return false;

    switch (formData.pricingType) {
      case "hourly":
        return formData.hourlyRate && !isNaN(formData.hourlyRate) && formData.hourlyRate > 0;
      case "session":
        return formData.sessionRate && !isNaN(formData.sessionRate) && formData.sessionRate > 0;
      case "level":
        return (
          formData.levelRates &&
          formData.levelRates.beginner &&
          !isNaN(formData.levelRates.beginner) &&
          formData.levelRates.beginner > 0 &&
          formData.levelRates.intermediate &&
          !isNaN(formData.levelRates.intermediate) &&
          formData.levelRates.intermediate > 0 &&
          formData.levelRates.advanced &&
          !isNaN(formData.levelRates.advanced) &&
          formData.levelRates.advanced > 0
        );
      case "monthly":
        return formData.monthlyRate && !isNaN(formData.monthlyRate) && formData.monthlyRate > 0;
      default:
        return false;
    }
  };

  const getPricingValidationErrors = () => {
    const errors = [];

    // Pricing type is mandatory for all tutors
    if (!formData.pricingType || formData.pricingType.trim() === "") {
      errors.push("Pricing type is required");
      return errors;
    }

    // Validate rates based on pricing type - all must be numbers > 0
    switch (formData.pricingType) {
      case "hourly":
        if (!formData.hourlyRate || isNaN(formData.hourlyRate) || formData.hourlyRate <= 0) {
          errors.push("Hourly rate is required and must be a number greater than 0");
        }
        break;
      case "session":
        if (!formData.sessionRate || isNaN(formData.sessionRate) || formData.sessionRate <= 0) {
          errors.push("Session rate is required and must be a number greater than 0");
        }
        break;
      case "level":
        if (
          !formData.levelRates ||
          !formData.levelRates.beginner ||
          isNaN(formData.levelRates.beginner) ||
          formData.levelRates.beginner <= 0 ||
          !formData.levelRates.intermediate ||
          isNaN(formData.levelRates.intermediate) ||
          formData.levelRates.intermediate <= 0 ||
          !formData.levelRates.advanced ||
          isNaN(formData.levelRates.advanced) ||
          formData.levelRates.advanced <= 0
        ) {
          errors.push("All level rates are required and must be numbers greater than 0");
        }
        break;
      case "monthly":
        if (!formData.monthlyRate || isNaN(formData.monthlyRate) || formData.monthlyRate <= 0) {
          errors.push("Monthly rate is required and must be a number greater than 0");
        }
        break;
    }

    return errors;
  };

  // Validation error for session preference
  const getSessionPreferenceValidationError = () => {
    if (user?.role !== "tutor") return "";
    if (!formData.sessionPreference || formData.sessionPreference.trim() === "") {
      return "Session preference is required";
    }
    return "";
  };

  // Clear draft function
  const handleClearDraft = () => {
    const draftKey = getDraftKey();
    localStorage.removeItem(draftKey);
    setLastSavedAt(null);
    toast({
      title: "Draft Cleared",
      description: "Your saved draft has been deleted. The form will reset to original data.",
      duration: 3000,
    });
    // Reset form to original tutorData
    setFormData({
      ...tutorData,
      hourlyRate: tutorData.hourlyRate || 5,
      availability: {
        days: tutorData.availability?.days || [],
        timeSlots: tutorData.availability?.timeSlots || [],
      },
      subjects: tutorData.subjects || [],
      languages: tutorData.languages || [],
      education: tutorData.education || [],
      country: tutorData.country || "EG", // Default to Egypt
      timezone: tutorData.timezone || "",
      personalEmail: tutorData.personalEmail || "",
      phoneNumber: tutorData.phoneNumber || "",
      sessionPreference: tutorData.sessionPreference || "both", // Default to chat & video
      pricingType: tutorData.pricingType || "hourly", // Default to hourly pricing
      sessionRate: tutorData.sessionRate || 0,
      levelRates: tutorData.levelRates || { beginner: 0, intermediate: 0, advanced: 0 },
      monthlyRate: tutorData.monthlyRate || 0,
      paymentMethods: tutorData.paymentMethods || [],
      grades: tutorData.grades || [],
      pricingCurrency: tutorData.pricingCurrency || "USD",
    });
  };

  const formatLastSaved = () => {
    if (!lastSavedAt) return "";
    const now = new Date();
    const diffMs = now.getTime() - lastSavedAt.getTime();
    const diffSecs = Math.floor(diffMs / 1000);

    if (diffSecs < 60) return "just now";
    const diffMins = Math.floor(diffSecs / 60);
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? "s" : ""} ago`;
    const diffHours = Math.floor(diffMins / 60);
    return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  };

  const defaultSubjects = Object.values(subjectCategories).flat();

  // Filter out subjects that are already in the main subjects collection to avoid duplicates
  const uniqueApprovedSubjects = approvedSubjects.filter(
    (subject) => !subjects.some((s) => s.name === subject) && !defaultSubjects.includes(subject),
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto p-3 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Edit Profile</span>
            {lastSavedAt && (
              <Badge variant="secondary" className="text-xs bg-green-50 text-green-700 border-green-200">
                ✓ Draft saved {formatLastSaved()}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Update your tutor profile information. Your changes are auto-saved and will be submitted for admin approval.
          </DialogDescription>
        </DialogHeader>

        {/* WhatsApp Contact Support - Smaller Version */}
        {supportContacts.length > 0 && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-2">
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <p className="text-xs font-medium text-green-900 mb-0.5">Need help? Contact us</p>
                <p className="text-[10px] font-medium text-green-900 mb-1" dir="rtl" style={{ textAlign: "right" }}>
                  إذا كنت بحاجة إلى مساعدة، تواصل معنا
                </p>
                <p className="text-[9px] text-green-800 mb-0.5">Only through WhatsApp</p>
                <p className="text-[9px] text-green-800 mb-1.5">We don't receive calls</p>
                <div className="flex flex-col gap-1.5">
                  {supportContacts.map((contact) => (
                    <div key={contact.id} className="flex items-center gap-1.5 flex-wrap">
                      <Button
                        onClick={() => {
                          navigator.clipboard.writeText(contact.phone_number);
                          setCopiedContactId(contact.id);
                          setTimeout(() => setCopiedContactId(null), 2000);
                          toast({
                            title: "Number Copied",
                            description: `${contact.phone_number} copied to clipboard`,
                          });
                        }}
                        className="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded-md text-[10px] font-medium transition-colors h-auto"
                      >
                        {copiedContactId === contact.id ? (
                          <>
                            <CheckCircle className="h-2.5 w-2.5" />
                            Copied
                          </>
                        ) : (
                          <>
                            <svg className="h-2.5 w-2.5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                            {contact.label || "Copy"}
                          </>
                        )}
                      </Button>
                      <span className="text-[11px] font-semibold text-green-900">{contact.phone_number}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* Admin Review Notice */}
          {hasActivePendingUpdate === true && (
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-4 w-4 text-blue-600" />
                <p className="font-medium text-blue-800">Pending Admin Review</p>
              </div>
              <p className="text-sm text-blue-700">
                Your previous profile changes are awaiting admin approval. Any new changes will also be submitted for
                review.
              </p>
            </div>
          )}

          {/* Rejected Profile Notice */}
          {hasRejection && !hasActivePendingUpdate && (
            <div className="border rounded-lg p-4 bg-orange-50 border-orange-200">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-orange-600" />
                <p className="font-medium text-orange-800">Previous Submission Rejected</p>
              </div>
              <p className="text-sm text-orange-700">
                Your previous profile update was rejected by admin. You can make changes and resubmit your profile for
                review.
              </p>
            </div>
          )}

          {/* Name and Country Fields with Cyan Color */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="p-3 sm:p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
              <Label htmlFor="name" className="text-cyan-700 dark:text-cyan-400 font-semibold text-sm">
                Name
                {user?.role === "tutor" && <span className="text-red-500 ml-1">*</span>}
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="Your full name"
                className="mt-2"
              />
            </div>
            <div className="p-3 sm:p-4 rounded-lg bg-teal-500/10 border border-teal-500/30">
              <Label className="flex items-center text-teal-700 dark:text-teal-400 font-semibold text-sm">
                <MapPin className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                Country
                {user?.role === "tutor" && <span className="text-red-500 ml-1">*</span>}
              </Label>
              <CountryCombobox
                value={formData.country}
                onValueChange={(value) => handleInputChange("country", value)}
                className="mt-2"
                placeholder="Select your country"
              />
            </div>
          </div>

          {/* Timezone Selector - Only show for countries with multiple timezones */}
          {formData.country && hasMultipleTimezones(formData.country) && (
            <div className="p-3 sm:p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
              <Label className="flex items-center text-purple-700 dark:text-purple-400 font-semibold text-sm">
                <Clock className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                Timezone
                {user?.role === "tutor" && <span className="text-red-500 ml-1">*</span>}
              </Label>
              <p className="text-xs text-purple-600 mt-1 mb-2">
                Your country has multiple timezones. Please select your specific timezone so students can see the
                correct times.
              </p>
              <Select value={formData.timezone || ""} onValueChange={(value) => handleInputChange("timezone", value)}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select your timezone">
                    {formData.timezone && getTimezoneDisplayName(formData.timezone)}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-[200px] overflow-y-auto bg-background z-[100]">
                  {getCountryTimezones(formData.country).map((tz) => (
                    <SelectItem key={tz} value={tz}>
                      {getTimezoneDisplayName(tz)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Profile Photo Section */}
          <div ref={photoSectionRef} className="border rounded-lg p-3 sm:p-4 bg-blue-50 border-blue-200">
            <div className="flex items-center gap-2 mb-3">
              <Camera className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
              <Label className="font-medium text-blue-800 text-sm">
                Profile Photo
                {user?.role === "tutor" && <span className="text-red-500 ml-1">*</span>}
              </Label>
            </div>
            <p className="text-xs text-blue-700 mb-4">
              Upload a professional photo that will be visible to students. This helps build trust and connection.
            </p>

            <div className="flex flex-col sm:flex-row items-start gap-4">
              {/* Photo Preview */}
              <div className="flex-shrink-0 mx-auto sm:mx-0">
                <Avatar className="w-20 h-20 sm:w-24 sm:h-24 border-2 border-blue-200">
                  <AvatarImage src={getCurrentPhotoUrl()} alt="Profile photo" />
                  <AvatarFallback className="bg-blue-100 text-blue-600">
                    <Camera className="h-6 w-6 sm:h-8 sm:w-8" />
                  </AvatarFallback>
                </Avatar>
              </div>

              {/* Upload Controls */}
              <div className="flex-1 w-full space-y-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />

                {/* File Upload Option */}
                <div>
                  <Label className="text-xs sm:text-sm font-medium mb-2 block">Upload from Device</Label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-2 w-full sm:w-auto flex-1 sm:flex-none"
                    >
                      <Upload className="h-3 w-3 sm:h-4 sm:w-4" />
                      {getCurrentPhotoUrl() ? "Change Photo" : "Upload Photo"}
                    </Button>

                    {getCurrentPhotoUrl() && (
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={removePhoto}
                        className="w-full sm:w-auto"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* URL Input Option */}
                <div>
                  <Label className="text-xs sm:text-sm font-medium mb-2 block">Or enter photo URL</Label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      placeholder="https://example.com/photo.jpg"
                      value={photoUrl}
                      onChange={(e) => setPhotoUrl(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handlePhotoUrlSubmit}
                      disabled={!photoUrl.trim()}
                      className="flex items-center gap-2 w-full sm:w-auto"
                    >
                      <Link className="h-3 w-3 sm:h-4 sm:w-4" />
                      Use URL
                    </Button>
                  </div>
                </div>

                <p className="text-xs text-gray-600">Supported formats: JPEG, PNG, GIF, WebP. Maximum size: 5MB.</p>
              </div>
            </div>
          </div>

          {/* Contact Information Section */}
          <div ref={contactSectionRef} className="border rounded-lg p-3 sm:p-4 bg-amber-50 border-amber-200">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4 text-amber-600" />
              <Label className="font-medium text-amber-800 text-sm">Contact Information (Admin Only)</Label>
            </div>
            <p className="text-xs text-amber-700 mb-4">
              This information is only visible to administrators for verification and support purposes. It will not be
              displayed publicly.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label htmlFor="personalEmail">
                  Personal Email
                  {user?.role === "tutor" && <span className="text-red-500 ml-1">*</span>}
                </Label>
                <Input
                  id="personalEmail"
                  type="email"
                  value={formData.personalEmail}
                  onChange={(e) => handleInputChange("personalEmail", e.target.value)}
                  placeholder="your.personal@email.com"
                  className={`${!formData.personalEmail.trim() && user?.role === "tutor" ? "border-red-500" : ""}`}
                />
              </div>
              <div>
                <Label htmlFor="phoneNumber">
                  Phone Number
                  {user?.role === "tutor" && <span className="text-red-500 ml-1">*</span>}
                </Label>
                <Input
                  id="phoneNumber"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className={`${!formData.phoneNumber.trim() && user?.role === "tutor" ? "border-red-500" : ""}`}
                />
              </div>
            </div>
          </div>

          {/* Basic Info - About Section with Primary Color */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div ref={shortBioSectionRef} className="p-3 sm:p-4 rounded-lg bg-primary/5 border border-primary/20">
              <Label htmlFor="shortBio" className="text-primary font-semibold text-sm">
                Short Bio
                {user?.role === "tutor" && <span className="text-red-500 ml-1">*</span>}
              </Label>
              <Input
                id="shortBio"
                value={formData.shortBio}
                onChange={(e) => handleInputChange("shortBio", e.target.value)}
                className={`${!formData.shortBio.trim() && user?.role === "tutor" ? "border-red-500" : ""} mt-2`}
                required
              />
            </div>
            <div
              ref={pricingSectionRef}
              className="p-3 sm:p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30"
            >
              <Label className="flex items-center mb-2 text-emerald-700 dark:text-emerald-400 font-semibold text-sm">
                <CreditCard className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                Pricing Structure
                {user?.role === "tutor" && <span className="text-red-500 ml-1">*</span>}
              </Label>

              {/* Pricing Guidance */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <div className="bg-green-100 rounded-full p-1 mt-0.5 flex-shrink-0">
                    <CreditCard className="h-3 w-3 text-green-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-green-800">
                      <p className="font-medium mb-1">💡 Pricing Tips:</p>
                      <ul className="space-y-1 text-xs">
                        <li>
                          • <strong>Hourly:</strong> Traditional per-hour rate
                        </li>
                        <li>
                          • <strong>Session:</strong> Fixed lesson price (45-60 min)
                        </li>
                        <li>
                          • <strong>Level:</strong> Different rates by skill level
                        </li>
                        <li>
                          • <strong>Monthly:</strong> Subscription for regular students
                        </li>
                      </ul>
                    </div>

                    {/* Pricing Guide */}
                    <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-md">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                        <div className="bg-amber-100 rounded-full p-1 flex-shrink-0">
                          <DollarSign className="h-3 w-3 text-amber-600" />
                        </div>
                        <div className="text-xs text-amber-800 min-w-0">
                          <p className="font-medium">Pricing Guide:</p>
                          <p className="break-words">
                            Choose and match your price in local currency with dollar.{" "}
                            <strong>Don't write with your local currency. Estimate it in US dollar.</strong> All prices
                            will be displayed in USD to students.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pricing Type Selection */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="pricingType" className="text-xs sm:text-sm font-medium mb-2 block">
                    Pricing Type
                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Select
                    value={formData.pricingType}
                    onValueChange={(value) => handleInputChange("pricingType", value)}
                  >
                    <SelectTrigger
                      className={`${!formData.pricingType || formData.pricingType.trim() === "" ? "border-red-500" : ""}`}
                    >
                      <SelectValue placeholder="Select your pricing model..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white z-50">
                      <SelectItem value="hourly">💰 By Hour</SelectItem>
                      <SelectItem value="session">📚 By Session</SelectItem>
                      <SelectItem value="level">🎯 By Level</SelectItem>
                      <SelectItem value="monthly">📅 Monthly Subscription</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Currency Selection - Temporarily disabled, defaults to USD */}
                <div style={{ display: "none" }}>
                  <Label htmlFor="pricingCurrency" className="text-sm font-medium mb-2 block">
                    Pricing Currency
                  </Label>
                  <Select value="USD" onValueChange={(value) => handleInputChange("pricingCurrency", "USD")} disabled>
                    <SelectTrigger>
                      <SelectValue placeholder="USD" />
                    </SelectTrigger>
                    <SelectContent className="bg-white z-50">
                      <SelectItem value="USD">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-primary">$</span>
                          <span className="text-muted-foreground">USD</span>
                          <span className="text-xs">- US Dollar</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Dynamic Pricing Inputs */}
                {formData.pricingType === "hourly" && (
                  <div>
                    <Label htmlFor="hourlyRate" className="text-xs sm:text-sm font-medium">
                      Hourly Rate ({currencies.find((c) => c.code === formData.pricingCurrency)?.symbol || "$"})
                      <span className="text-red-500 ml-1">*</span>
                    </Label>
                    <Input
                      id="hourlyRate"
                      type="number"
                      min="1"
                      step="0.5"
                      value={formData.hourlyRate === 0 ? "" : formData.hourlyRate.toString()}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "") {
                          handleInputChange("hourlyRate", 0);
                        } else {
                          const numValue = parseFloat(value);
                          if (!isNaN(numValue) && numValue > 0) {
                            handleInputChange("hourlyRate", numValue);
                          }
                        }
                      }}
                      placeholder="e.g., 25 or 25.5 (must be greater than 0)"
                      className={`${(!formData.hourlyRate || formData.hourlyRate <= 0) && formData.pricingType === "hourly" ? "border-red-500" : ""}`}
                    />
                    {(!formData.hourlyRate || formData.hourlyRate <= 0) && formData.pricingType === "hourly" && (
                      <p className="text-sm text-red-500 mt-1">Hourly rate is required and must be greater than 0</p>
                    )}
                  </div>
                )}

                {formData.pricingType === "session" && (
                  <div>
                    <Label htmlFor="sessionRate" className="text-xs sm:text-sm font-medium">
                      Per Session Rate ({currencies.find((c) => c.code === formData.pricingCurrency)?.symbol || "$"})
                    </Label>
                    <Input
                      id="sessionRate"
                      type="number"
                      min="0"
                      step="1"
                      value={formData.sessionRate === 0 ? "" : formData.sessionRate?.toString()}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "") {
                          handleInputChange("sessionRate", 0);
                        } else {
                          const numValue = parseInt(value);
                          if (!isNaN(numValue) && numValue >= 0) {
                            handleInputChange("sessionRate", numValue);
                          }
                        }
                      }}
                      placeholder="e.g., 50"
                    />
                    <p className="text-xs text-gray-600 mt-1">Price per individual session</p>
                  </div>
                )}

                {formData.pricingType === "level" && (
                  <div className="space-y-3">
                    <Label className="text-xs sm:text-sm font-medium">
                      Rates by Student Level (
                      {currencies.find((c) => c.code === formData.pricingCurrency)?.symbol || "$"})
                    </Label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                      <div>
                        <Label htmlFor="beginnerRate" className="text-xs">
                          Beginner
                        </Label>
                        <Input
                          id="beginnerRate"
                          type="number"
                          min="0"
                          step="1"
                          value={formData.levelRates?.beginner === 0 ? "" : formData.levelRates?.beginner?.toString()}
                          onChange={(e) => {
                            const value = e.target.value;
                            const numValue = value === "" ? 0 : parseInt(value);
                            if (!isNaN(numValue) && numValue >= 0) {
                              handleInputChange("levelRates", {
                                ...formData.levelRates,
                                beginner: numValue,
                              });
                            }
                          }}
                          placeholder="e.g., 20"
                        />
                      </div>
                      <div>
                        <Label htmlFor="intermediateRate" className="text-xs">
                          Intermediate
                        </Label>
                        <Input
                          id="intermediateRate"
                          type="number"
                          min="0"
                          step="1"
                          value={
                            formData.levelRates?.intermediate === 0 ? "" : formData.levelRates?.intermediate?.toString()
                          }
                          onChange={(e) => {
                            const value = e.target.value;
                            const numValue = value === "" ? 0 : parseInt(value);
                            if (!isNaN(numValue) && numValue >= 0) {
                              handleInputChange("levelRates", {
                                ...formData.levelRates,
                                intermediate: numValue,
                              });
                            }
                          }}
                          placeholder="e.g., 30"
                        />
                      </div>
                      <div>
                        <Label htmlFor="advancedRate" className="text-xs">
                          Advanced
                        </Label>
                        <Input
                          id="advancedRate"
                          type="number"
                          min="0"
                          step="1"
                          value={formData.levelRates?.advanced === 0 ? "" : formData.levelRates?.advanced?.toString()}
                          onChange={(e) => {
                            const value = e.target.value;
                            const numValue = value === "" ? 0 : parseInt(value);
                            if (!isNaN(numValue) && numValue >= 0) {
                              handleInputChange("levelRates", {
                                ...formData.levelRates,
                                advanced: numValue,
                              });
                            }
                          }}
                          placeholder="e.g., 40"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {formData.pricingType === "monthly" && (
                  <div>
                    <Label htmlFor="monthlyRate" className="text-xs sm:text-sm font-medium">
                      Monthly Subscription ({currencies.find((c) => c.code === formData.pricingCurrency)?.symbol || "$"}
                      )
                    </Label>
                    <Input
                      id="monthlyRate"
                      type="number"
                      min="0"
                      step="1"
                      value={formData.monthlyRate === 0 ? "" : formData.monthlyRate?.toString()}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "") {
                          handleInputChange("monthlyRate", 0);
                        } else {
                          const numValue = parseInt(value);
                          if (!isNaN(numValue) && numValue >= 0) {
                            handleInputChange("monthlyRate", numValue);
                          }
                        }
                      }}
                      placeholder="e.g., 200"
                    />
                    <p className="text-xs text-gray-600 mt-1">Monthly unlimited access price</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Session Preference with Violet Color */}
          <div ref={sessionSectionRef} className="p-3 sm:p-4 rounded-lg bg-violet-500/10 border border-violet-500/30">
            <Label
              htmlFor="sessionPreference"
              className="flex items-center mb-2 text-violet-700 dark:text-violet-400 font-semibold text-sm"
            >
              <MessageCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
              Session Preference
              {user?.role === "tutor" && <span className="text-red-500 ml-1">*</span>}
            </Label>

            {/* Guidance Instructions */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <div className="bg-purple-100 rounded-full p-1 mt-0.5">
                  <MessageCircle className="h-3 w-3 text-purple-600" />
                </div>
                <div className="text-sm text-purple-800">
                  <p className="font-medium mb-1">💡 Choose Your Teaching Style:</p>
                  <ul className="space-y-1 text-xs">
                    <li>
                      • <strong>Chat & Video:</strong> Flexible - offer both text and video sessions
                    </li>
                    <li>
                      • <strong>Chat Only:</strong> Prefer text-based teaching and messaging
                    </li>
                    <li>
                      • <strong>Video Only:</strong> Prefer face-to-face video sessions
                    </li>
                    <li>• This helps students understand your teaching approach</li>
                  </ul>
                </div>
              </div>
            </div>

            {user?.role === "tutor" && getSessionPreferenceValidationError() && (
              <p className="text-sm text-red-600 mb-2">{getSessionPreferenceValidationError()}</p>
            )}

            <Select
              value={formData.sessionPreference}
              onValueChange={(value) => handleInputChange("sessionPreference", value)}
            >
              <SelectTrigger
                className={`${getSessionPreferenceValidationError() ? "border-red-500 focus:border-red-500" : ""}`}
              >
                <SelectValue placeholder="Select your preferred session type..." />
              </SelectTrigger>
              <SelectContent className="bg-white z-50">
                <SelectItem value="both">💬📹 Chat & Video Sessions</SelectItem>
                <SelectItem value="chat_only">💬 Chat & Messages Only</SelectItem>
                <SelectItem value="video_only">📹 Video Sessions Only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Full Bio with Indigo Color */}
          <div ref={bioSectionRef} className="p-3 sm:p-4 rounded-lg bg-indigo-500/10 border border-indigo-500/30">
            <Label htmlFor="bio" className="text-indigo-700 dark:text-indigo-400 font-semibold text-sm">
              Full Bio
              {user?.role === "tutor" && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Textarea
              id="bio"
              value={formData.bio}
              onChange={(e) => handleInputChange("bio", e.target.value)}
              className={`min-h-[100px] w-full resize-none text-base mt-2 ${!formData.bio.trim() && user?.role === "tutor" ? "border-red-500" : ""}`}
              style={{ fontSize: "16px" }}
              required
            />
          </div>

          {/* New Subject Selection Style - Sky Color */}
          <div ref={subjectsSectionRef} className="p-3 sm:p-4 rounded-lg bg-sky-500/10 border border-sky-500/30">
            <Label className="flex items-center mb-4 text-sky-700 dark:text-sky-400 font-semibold text-sm">
              <BookOpen className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
              Subjects
            </Label>

            {/* Subject Guide */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <div className="bg-blue-100 rounded-full p-1 mt-0.5 flex-shrink-0">
                  <BookOpen className="h-3 w-3 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">📚 Subject Guide:</p>
                    <ul className="space-y-1 text-xs">
                      <li>• Select subjects you're qualified to teach</li>
                      <li>
                        • Missing subject? <strong>Request it to be added!</strong>
                      </li>
                      <li>• Admin will review new subject requests</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Selected Subjects Display */}
            <div className="flex flex-wrap gap-2 mb-4 p-3 border rounded-lg bg-gray-50 min-h-[50px]">
              {formData.subjects.length > 0 ? (
                formData.subjects.map((subject) => (
                  <Badge key={subject} variant="default" className="flex items-center gap-1">
                    {subject}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeSubject(subject)} />
                  </Badge>
                ))
              ) : (
                <p className="text-gray-500 text-sm">No subjects selected</p>
              )}
            </div>

            {/* Subject Categories - Compact with Almost White Background */}
            <div className="border rounded-lg bg-white">
              {Object.entries(subjectCategories).map(([category, subjects]) => (
                <div key={category} className="p-2 border-b last:border-b-0 bg-slate-50/30">
                  <h4 className="font-medium text-xs text-gray-700 mb-2 flex items-center justify-between">
                    <span>{category}</span>
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-gray-100 text-gray-600">
                      {subjects.filter((s) => formData.subjects.includes(s)).length}/{subjects.length}
                    </Badge>
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1">
                    {subjects.map((subject) => (
                      <label
                        key={subject}
                        htmlFor={`subject-${subject}`}
                        className="flex items-center space-x-1.5 p-1.5 rounded hover:bg-gray-50 cursor-pointer transition-colors text-xs break-words"
                      >
                        <Checkbox
                          id={`subject-${subject}`}
                          checked={formData.subjects.includes(subject)}
                          onCheckedChange={(checked) => handleSubjectToggle(subject, checked as boolean)}
                          className="h-3.5 w-3.5 flex-shrink-0"
                        />
                        <span className="text-xs leading-tight">{subject}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}

              {/* Approved subjects from subjects collection */}
              {subjects.filter((s) => s.category === "approved").length > 0 && (
                <div className="p-2 bg-slate-50/30 border-b">
                  <h4 className="font-medium text-xs text-gray-700 mb-2 flex items-center gap-1.5">
                    <Badge
                      variant="outline"
                      className="text-[10px] px-1.5 py-0 bg-gray-100 text-gray-600 border-gray-200"
                    >
                      Approved
                    </Badge>
                    Newly Approved Subjects
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1">
                    {subjects
                      .filter((s) => s.category === "approved")
                      .map((subject) => (
                        <label
                          key={subject.id}
                          htmlFor={`new-approved-${subject.id}`}
                          className="flex items-center space-x-1.5 p-1.5 rounded hover:bg-gray-50 cursor-pointer transition-colors text-xs break-words"
                        >
                          <Checkbox
                            id={`new-approved-${subject.id}`}
                            checked={formData.subjects.includes(subject.name)}
                            onCheckedChange={(checked) => handleSubjectToggle(subject.name, checked as boolean)}
                            className="h-3.5 w-3.5 flex-shrink-0"
                          />
                          <span className="text-xs font-medium text-gray-700 leading-tight">{subject.name}</span>
                        </label>
                      ))}
                  </div>
                </div>
              )}

              {/* Legacy approved subjects */}
              {isLoadingModalData ? (
                <div className="p-2 bg-slate-50/30 border-t-2 border-gray-100">
                  <div className="animate-pulse">
                    <div className="h-3 bg-gray-200 rounded w-1/3 mb-2"></div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1">
                      {[1, 2, 3, 4, 5, 6].map((i) => (
                        <div key={i} className="h-5 bg-gray-200 rounded"></div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : uniqueApprovedSubjects.length > 0 ? (
                <div className="p-2 bg-slate-50/30 border-t">
                  <h4 className="font-medium text-xs text-gray-700 mb-2 flex items-center gap-1.5">
                    <Badge
                      variant="outline"
                      className="text-[10px] px-1.5 py-0 bg-gray-100 text-gray-600 border-gray-200"
                    >
                      Approved
                    </Badge>
                    Approved Specialized Subjects
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1">
                    {uniqueApprovedSubjects.map((subject) => (
                      <label
                        key={subject}
                        htmlFor={`approved-${subject}`}
                        className="flex items-center space-x-1.5 p-1.5 rounded hover:bg-gray-50 cursor-pointer transition-colors text-xs break-words"
                      >
                        <Checkbox
                          id={`approved-${subject}`}
                          checked={formData.subjects.includes(subject)}
                          onCheckedChange={(checked) => handleSubjectToggle(subject, checked as boolean)}
                          className="h-3.5 w-3.5 flex-shrink-0"
                        />
                        <span className="text-xs font-medium text-gray-700 leading-tight">{subject}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            {/* Subject Validation Error */}
            {getSubjectValidationError() && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600 font-medium">{getSubjectValidationError()}</p>
              </div>
            )}

            {/* Custom Subject Request */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <Label className="text-sm font-medium">Request New Subject</Label>
              </div>
              <p className="text-xs text-gray-600 mb-2">
                Can't find your subject? Request a new one - it will need admin approval.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  placeholder="Enter new subject name"
                  value={customSubject}
                  onChange={(e) => setCustomSubject(e.target.value)}
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={addCustomSubject}
                  size="sm"
                  disabled={!customSubject.trim()}
                  variant="outline"
                  className="w-full sm:w-auto"
                >
                  Request
                </Button>
              </div>
            </div>
          </div>

          {/* Teaching Languages Section with Rose Color */}
          <div ref={languagesSectionRef} className="p-3 sm:p-4 rounded-lg bg-rose-500/10 border border-rose-500/30">
            <Label className="text-rose-700 dark:text-rose-400 font-semibold text-sm">
              Teaching Language
              {user?.role === "tutor" && <span className="text-red-500 ml-1">*</span>}
            </Label>

            <div className="flex flex-wrap gap-2 mb-3 p-3 border rounded-lg bg-gray-50 min-h-[60px]">
              {formData.languages.length > 0 ? (
                formData.languages.map((language) => (
                  <Badge key={language} variant="secondary" className="flex items-center gap-1 text-sm">
                    {language}
                    <X className="h-3 w-3 cursor-pointer hover:text-red-600" onClick={() => removeLanguage(language)} />
                  </Badge>
                ))
              ) : (
                <p className="text-gray-500 text-sm">Select languages you can teach in</p>
              )}
            </div>

            <Select
              value={newLanguage}
              onValueChange={(value) => {
                if (value && !formData.languages.includes(value)) {
                  setFormData((prev) => ({
                    ...prev,
                    languages: [...prev.languages, value],
                  }));
                }
                setNewLanguage("");
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="+ Add teaching language" />
              </SelectTrigger>
              <SelectContent className="max-h-[200px]">
                {availableLanguages
                  .filter((language) => !formData.languages.includes(language))
                  .map((language) => (
                    <SelectItem key={language} value={language}>
                      {language}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* Education Section with Green Color */}
          <div ref={educationSectionRef} className="p-3 sm:p-4 rounded-lg bg-green-500/10 border border-green-500/30">
            <Label className="flex items-center mb-3 text-green-700 dark:text-green-400 font-semibold text-sm">
              <GraduationCap className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
              Education
            </Label>

            <div className="space-y-2 mb-3 p-3 border rounded-lg bg-gray-50 min-h-[60px]">
              {formData.education.length > 0 ? (
                formData.education.map((edu, index) => (
                  <div key={index} className="flex items-center justify-between p-2 border rounded bg-white">
                    <div className="flex-1">
                      <div className="font-medium text-sm">{edu.degree}</div>
                      <div className="text-xs text-gray-600">
                        {edu.institution} • {edu.year}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeEducation(index)} className="h-8 w-8 p-0">
                      <X className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-sm">Add your education credentials</p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              <Select
                value={newEducation.degree}
                onValueChange={(value) => setNewEducation((prev) => ({ ...prev, degree: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select degree" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  {degreeOptions.map((degree) => (
                    <SelectItem key={degree} value={degree}>
                      {degree}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder="Institution name"
                value={newEducation.institution}
                onChange={(e) => setNewEducation((prev) => ({ ...prev, institution: e.target.value }))}
              />
              <Input
                type="number"
                placeholder="Year"
                min="1950"
                max={new Date().getFullYear() + 5}
                value={newEducation.year}
                onChange={(e) =>
                  setNewEducation((prev) => ({ ...prev, year: parseInt(e.target.value) || new Date().getFullYear() }))
                }
              />
            </div>

            {(newEducation.degree || newEducation.institution) && (
              <Button
                type="button"
                onClick={addEducation}
                size="sm"
                disabled={!newEducation.degree || !newEducation.institution}
                className="w-full mt-2"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Education
              </Button>
            )}
          </div>

          {/* Grades Section with Purple Color */}
          <div className="p-3 sm:p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
            <Label className="flex items-center mb-3 text-purple-700 dark:text-purple-400 font-semibold text-sm">
              <GraduationCap className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
              Grade Levels Taught
            </Label>

            <div className="flex flex-wrap gap-2 mb-3 p-3 border rounded-lg bg-gray-50 min-h-[60px]">
              {formData.grades && formData.grades.length > 0 ? (
                formData.grades.map((grade) => (
                  <Badge key={grade} variant="secondary" className="flex items-center gap-1 text-sm">
                    {grade}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-red-600"
                      onClick={() => {
                        const updatedGrades = formData.grades?.filter((g) => g !== grade) || [];
                        handleInputChange("grades", updatedGrades);
                      }}
                    />
                  </Badge>
                ))
              ) : (
                <p className="text-gray-500 text-sm">Select grade levels you teach</p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {[
                "Preschool (3-5)",
                "Elementary (K-5)",
                "Middle School (6-8)",
                "High School (9-12)",
                "College/University",
                "Adult Learners",
                "Professional Dev.",
                "All Ages",
              ].map((grade) => (
                <label
                  key={grade}
                  htmlFor={`grade-${grade}`}
                  className={`flex items-center space-x-2 p-2 rounded border cursor-pointer transition-colors ${
                    formData.grades?.includes(grade)
                      ? "bg-blue-50 border-blue-300"
                      : "bg-white border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <Checkbox
                    id={`grade-${grade}`}
                    checked={formData.grades?.includes(grade) || false}
                    onCheckedChange={(checked) => {
                      const currentGrades = formData.grades || [];
                      if (checked) {
                        handleInputChange("grades", [...currentGrades, grade]);
                      } else {
                        handleInputChange(
                          "grades",
                          currentGrades.filter((g) => g !== grade),
                        );
                      }
                    }}
                  />
                  <span className="text-xs flex-1">{grade}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Availability Section with Blue Color */}
          <div ref={availabilitySectionRef} className="p-3 sm:p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
            <Label className="flex items-center mb-3 text-blue-700 dark:text-blue-400 font-semibold text-sm">
              <Clock className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
              Availability Schedule
              {user?.role === "tutor" && <span className="text-red-500 ml-1">*</span>}
            </Label>

            {user?.role === "tutor" && getAvailabilityValidationError() && (
              <p className="text-sm text-red-600 mb-2">{getAvailabilityValidationError()}</p>
            )}

            <div className="space-y-3">
              {daysOfWeek.map((day) => {
                const daySlots = formData.availability.timeSlots?.find((slot) => slot.day === day);
                const hasSlots = daySlots && daySlots.slots.length > 0;
                return (
                  <div
                    key={day}
                    className={`border rounded-lg p-3 ${hasSlots ? "bg-blue-50 border-blue-200" : "bg-gray-50"}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-sm">{day}</h4>
                      <Button size="sm" variant="ghost" onClick={() => addTimeSlot(day)} className="h-7 text-xs">
                        <Plus className="h-3 w-3 mr-1" />
                        Add
                      </Button>
                    </div>
                    {hasSlots ? (
                      <div className="space-y-2">
                        {daySlots.slots.map((slot, slotIndex) => {
                          const dayIndex = formData.availability.timeSlots.findIndex((s) => s.day === day);
                          return (
                            <div
                              key={slotIndex}
                              className="flex flex-wrap sm:flex-nowrap items-center gap-2 bg-white p-2 rounded"
                            >
                              <Select
                                value={slot.start}
                                onValueChange={(value) => updateTimeSlot(dayIndex, slotIndex, "start", value)}
                              >
                                <SelectTrigger className="w-full sm:w-28 h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="max-h-[200px]">
                                  {timeOptions.map((time) => (
                                    <SelectItem key={time} value={time}>
                                      {time}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <span className="text-xs text-gray-500">to</span>
                              <Select
                                value={slot.end}
                                onValueChange={(value) => updateTimeSlot(dayIndex, slotIndex, "end", value)}
                              >
                                <SelectTrigger className="w-full sm:w-28 h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="max-h-[200px]">
                                  {timeOptions.map((time) => (
                                    <SelectItem key={time} value={time}>
                                      {time}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeTimeSlot(dayIndex, slotIndex)}
                                className="h-8 w-8 p-0 hover:bg-red-50"
                              >
                                <X className="h-3 w-3 text-red-500" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">Click "Add" to set time slots</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Teaching Experience Section with Yellow Color */}
          <div
            ref={experienceSectionRef}
            className="p-3 sm:p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30"
          >
            <Label htmlFor="experience" className="text-yellow-700 dark:text-yellow-400 font-semibold text-sm">
              Teaching Experience
            </Label>
            <Input
              id="experience"
              value={formData.experience}
              onChange={(e) => handleInputChange("experience", e.target.value)}
              placeholder="e.g., 5 years of teaching mathematics"
              className="mt-2"
            />
          </div>

          {/* Payment Methods Section with Orange Color */}
          {availablePaymentMethods.length > 0 && (
            <div className="p-3 sm:p-4 rounded-lg bg-orange-500/10 border border-orange-500/30">
              <Label className="flex items-center mb-3 text-orange-700 dark:text-orange-400 font-semibold text-sm">
                <CreditCard className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                Payment Methods
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {availablePaymentMethods.map((method) => (
                  <label
                    key={method.id}
                    htmlFor={`payment-${method.id}`}
                    className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      formData.paymentMethods?.includes(method.id)
                        ? "bg-blue-50 border-blue-300"
                        : "bg-white border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <Checkbox
                      id={`payment-${method.id}`}
                      checked={formData.paymentMethods?.includes(method.id) || false}
                      onCheckedChange={(checked) => {
                        const currentMethods = formData.paymentMethods || [];
                        const updatedMethods = checked
                          ? [...currentMethods, method.id]
                          : currentMethods.filter((m) => m !== method.id);
                        handleInputChange("paymentMethods", updatedMethods);
                      }}
                    />
                    <span className="text-sm font-medium flex-1">{method.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Introduction Video Section with Pink Color */}
          <div
            ref={videoSectionRef}
            className="p-3 sm:p-4 rounded-lg bg-pink-500/10 border border-pink-500/30 space-y-4"
          >
            <div>
              <Label className="text-pink-700 dark:text-pink-400 font-semibold mb-3 block text-sm">
                Introduction Video
              </Label>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-4">
                Upload a video file or paste a YouTube/Vimeo link
              </p>
            </div>

            {/* Video File Upload */}
            <div className="space-y-3">
              <input
                ref={videoInputRef}
                type="file"
                accept="video/mp4,video/webm,video/ogg,video/quicktime"
                onChange={handleVideoUpload}
                className="hidden"
              />

              {/* Upload Button */}
              <div>
                <Label className="text-xs sm:text-sm font-medium mb-2 block">Upload Video File</Label>
                <p className="text-[10px] sm:text-xs text-gray-500 mb-2">
                  Accepted formats: MP4, WebM, OGG, MOV (max 500MB)
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => videoInputRef.current?.click()}
                    className="flex items-center gap-2 w-full sm:w-auto"
                    disabled={!!formData.introVideo.trim()}
                  >
                    <Upload className="h-3 w-3 sm:h-4 sm:w-4" />
                    {selectedVideo ? "Change Video" : "Upload Video"}
                  </Button>

                  {selectedVideo && (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="w-full sm:w-auto"
                      onClick={removeVideo}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {selectedVideo && (
                  <p className="text-xs text-green-600 mt-1 font-medium">
                    ✓ {selectedVideo.name} ({(selectedVideo.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
                {formData.introVideo.trim() && !selectedVideo && (
                  <p className="text-xs text-amber-600 mt-1">Clear the URL below to enable video upload</p>
                )}
              </div>

              {/* OR Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-gray-500">Or</span>
                </div>
              </div>

              {/* Video URL Input */}
              <div>
                <Label htmlFor="introVideo" className="text-xs sm:text-sm font-medium mb-2 block">
                  Video URL (YouTube, Vimeo, Google Drive)
                </Label>
                <Input
                  id="introVideo"
                  value={formData.introVideo}
                  onChange={(e) => {
                    handleInputChange("introVideo", e.target.value);
                    if (e.target.value.trim()) {
                      removeVideo(); // Clear uploaded video when URL is entered
                    }
                  }}
                  placeholder="https://youtube.com/watch?v=..."
                  className="mt-1"
                  disabled={!!selectedVideo}
                />
                {selectedVideo && (
                  <p className="text-xs text-amber-600 mt-1">Remove uploaded video to use URL instead</p>
                )}
              </div>

              {/* Preview Section - Always Visible */}
              <div className="mt-3">
                <Label className="text-sm font-medium mb-2 block">Preview {videoPreview && "✓ Video Ready"}</Label>

                {/* Show video preview or unsupported message */}
                {selectedVideo && (
                  <div className="space-y-3">
                    <div className="aspect-video rounded-lg overflow-hidden bg-gray-900 border-2 border-green-500">
                      {!videoPreviewError && videoPreview ? (
                        <video
                          src={videoPreview}
                          className="w-full h-full object-contain"
                          controls
                          preload="auto"
                          playsInline
                          onLoadedMetadata={(e) => {
                            console.log("📹 Video loaded successfully");
                            const video = e.target as HTMLVideoElement;
                            console.log("📹 Duration:", video.duration);
                          }}
                          onError={(e) => {
                            console.log("📹 Preview not supported for this codec");
                            const video = e.target as HTMLVideoElement;
                            if (video.error?.code === 4) {
                              setVideoPreviewError(true);
                            }
                          }}
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full text-center p-6">
                          <div className="text-6xl mb-4">🎥</div>
                          <p className="text-white font-medium mb-2">Video Selected</p>
                          <p className="text-sm text-gray-400 mb-4">
                            Preview not available for this video format,
                            <br />
                            but your video will upload successfully
                          </p>
                          <div className="bg-white/10 rounded-lg p-3 text-xs text-white">
                            <div className="font-medium mb-1">{selectedVideo.name}</div>
                            <div className="text-gray-300">
                              {selectedVideo.type} • {(selectedVideo.size / 1024 / 1024).toFixed(2)} MB
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-center text-green-600 font-medium">✓ Video ready to upload</p>
                  </div>
                )}

                {/* Show URL preview if URL is provided */}
                {!videoPreview && formData.introVideo.trim() && (
                  <div className="aspect-video rounded-lg overflow-hidden bg-gray-900 border-2 border-gray-300">
                    {formData.introVideo.includes("drive.google.com") ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center text-white p-4">
                          <p className="text-sm mb-2">🎥 Google Drive video</p>
                          <p className="text-xs text-gray-400">
                            Preview not available - video will be embedded on your profile
                          </p>
                        </div>
                      </div>
                    ) : (
                      <iframe
                        src={formData.introVideo}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="Video preview"
                      />
                    )}
                  </div>
                )}

                {/* Show empty state if no video */}
                {!videoPreview && !formData.introVideo.trim() && (
                  <div className="aspect-video rounded-lg overflow-hidden bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
                    <p className="text-gray-500 text-sm">No video selected</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 flex-col sm:flex-row">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between w-full gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearDraft}
              disabled={isSaving}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 w-full sm:w-auto"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear Draft
            </Button>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button variant="outline" onClick={onClose} disabled={isSaving} className="flex-1 sm:flex-none">
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving || (user?.role === "tutor" && !isFormValid())}
                className="flex-1 sm:flex-none"
              >
                {isSaving ? "Saving..." : "Submit for Approval"}
              </Button>
            </div>
          </div>
          {user?.role === "tutor" && !isFormValid() && (
            <div className="w-full p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm font-medium text-red-600 mb-2">Please complete the following required fields:</p>
              <ul className="text-sm text-red-600 list-disc list-inside space-y-1">
                {getValidationErrors().map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditProfileModal;
